import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import AppLayout from "./components/layouts";
import AppRoutes from "./components/routes";
import Login from "./modules/auth/components/login";
import loadIcons from "./loadIcons";
import { useEffect, useMemo, useState } from "react";
import "./instrument";
import ForgetPassword from "./modules/auth/components/forget-password";
import SupportAccess from "./modules/auth/components/support-access";
import { useTranslation } from "react-i18next";
import { ConfigProvider } from "antd";
import { setTwoToneColor } from "@ant-design/icons";
import arEGBase from "antd/locale/ar_EG";
import enUS from "antd/locale/en_US";
import NetworkErrorTrigger from "components/shared/NetworkErrorTrigger";
import { useFcmToken } from "hooks";
import { setLanguage } from "services/general";
import { ThemePaletteContext } from "hooks/useThemePalette";
import { DEFAULT_THEME_PALETTE } from "constants/themes";
import {
  THEME_PALETTE_STORAGE_KEY,
  applyThemePalette,
  getStoredThemePalette,
  initThemePalette,
  persistThemePalette,
  readThemeColor,
} from "services/theme-palette";
import { applyFavicon, initFavicon, FAVICON_STORAGE_KEY } from "utils/favicon";
import { LOGIN_PATH, loginUrlFor } from "modules/auth/utils/loginRedirect";

const AR_SHORT_WEEK_DAYS = ["ح", "ن", "ث", "ر", "خ", "ج", "س"];

const arEG = {
  ...arEGBase,
  DatePicker: {
    ...arEGBase.DatePicker,
    lang: {
      ...arEGBase.DatePicker.lang,
      shortWeekDays: AR_SHORT_WEEK_DAYS,
    },
  },
  Calendar: {
    ...arEGBase.Calendar,
    lang: {
      ...arEGBase.Calendar.lang,
      shortWeekDays: AR_SHORT_WEEK_DAYS,
    },
  },
};

loadIcons();

// Reads the tenant favicon cached by a previous Settings visit (see
// utils/favicon.js) and applies it immediately — a window that opens
// directly on some other page must not wait for Settings to mount before it
// shows the tenant's own icon.
initFavicon();

// Whether this page load began signed in. A signed-out arrival is a link opened from outside
// (pasted, shared), and is the one case the login page returns to after signing in; a session
// that ends while the app is open — expired, or signed out from another tab — gets a plain
// login page.
const SIGNED_IN_ON_LOAD = localStorage.getItem("token") !== null;
initThemePalette({ signedIn: SIGNED_IN_ON_LOAD });

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const isAuthenticated = localStorage.getItem("token") !== null;
  let lng = localStorage.getItem("default-lg");

  const [themePalette, setThemePalette] = useState(getStoredThemePalette);

  // Call the FCM token registration hook
  useFcmToken(isAuthenticated);

  const changeThemePalette = (id) => {
    persistThemePalette(id);
    setThemePalette(id);
  };

  // A 401 signs out without a reload, so the applied palette follows the auth state.
  const activePalette = isAuthenticated ? themePalette : DEFAULT_THEME_PALETTE;

  // Built during render so nothing below reads a stale page: AntD derives its controls
  // from one real colour (not a CSS variable), and its static dialogs, toasts and
  // two-tone icons read AntD's global config rather than this tree's ConfigProvider.
  const antdTheme = useMemo(() => {
    applyThemePalette(activePalette);
    const primary = readThemeColor("--color-primary");
    const theme = {
      token: {
        colorPrimary: primary,
        colorLink: primary,
        colorInfo: readThemeColor("--color-info"),
      },
    };
    setTwoToneColor(primary);
    return theme;
  }, [activePalette]);

  const isArabic = i18n.language === "ar";

  // The static `message.*` / `notification.*` / `Modal.confirm` calls (hundreds of them)
  // render outside this tree. `holderRender` wraps each in a ConfigProvider carrying the
  // palette, direction and locale, so a toast on the Arabic page is RTL and themed — and
  // AntD stops warning that static functions cannot consume the dynamic theme.
  useMemo(() => {
    ConfigProvider.config({
      theme: antdTheme,
      holderRender: (children) => (
        <ConfigProvider
          direction={isArabic ? "rtl" : "ltr"}
          locale={isArabic ? arEG : enUS}
          theme={antdTheme}
        >
          {children}
        </ConfigProvider>
      ),
    });
  }, [antdTheme, isArabic]);

  useEffect(() => {
    if (!lng) {
      localStorage.setItem("default-lg", "ar");
    }
  }, [lng]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const savedLang = localStorage.getItem("default-lg") || "en";

    setLanguage(savedLang);
    i18n.changeLanguage(savedLang);
    document.documentElement.setAttribute("lang", savedLang);
    document.documentElement.setAttribute(
      "dir",
      savedLang === "ar" ? "rtl" : "ltr"
    );
    // Add language class to body for portal components (dropdowns, modals, etc.)
    document.body.className = savedLang;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update body class when language changes dynamically
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      document.body.className = lng;
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // `storage` only fires in OTHER windows/tabs, which is exactly the case
  // `initFavicon()` above cannot cover: a window already open elsewhere when
  // the favicon is saved from this one.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === FAVICON_STORAGE_KEY) {
        applyFavicon(event.newValue);
      }
      if (event.key === THEME_PALETTE_STORAGE_KEY) {
        setThemePalette(getStoredThemePalette());
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const pathParts = location.pathname.split("/").filter(Boolean);
  const firstSegment = pathParts[0];
  const validLangs = ["en", "ar"];

  // Sync URL locale to app when pathname has a valid locale different from current language
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!firstSegment || !validLangs.includes(firstSegment)) return;
    if (i18n.language === firstSegment) return;

    localStorage.setItem("default-lg", firstSegment);
    setLanguage(firstSegment);
    i18n.changeLanguage(firstSegment);
    document.documentElement.setAttribute("lang", firstSegment);
    document.documentElement.setAttribute(
      "dir",
      firstSegment === "ar" ? "rtl" : "ltr"
    );
    document.body.className = firstSegment;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, firstSegment, i18n]);

  // Ahead of both branches below, and ahead of the invalid-prefix redirect, because either
  // would discard the arrival: unauthenticated, the catch-all sends it to /login and the
  // fragment with it; already authenticated, "support-access" is not a language prefix.
  if (location.pathname.replace(/\/+$/, "") === "/support-access") {
    return <SupportAccess />;
  }

  if (firstSegment && !validLangs.includes(firstSegment) && isAuthenticated) {
    // Invalid lang prefix, redirect to home or /<default-lang>
    return <Navigate to="/" replace />;
  }

  return (
    <ThemePaletteContext.Provider value={{ themePalette, changeThemePalette }}>
      <ConfigProvider
        direction={isArabic ? "rtl" : "ltr"}
        locale={isArabic ? arEG : enUS}
        theme={antdTheme}
      >
        <NetworkErrorTrigger />
        {isAuthenticated ? (
          <AppLayout>
            <AppRoutes />
          </AppLayout>
        ) : (
          <Routes>
            <Route
              path="*"
              exact={true}
              element={
                <Navigate
                  replace
                  to={SIGNED_IN_ON_LOAD ? LOGIN_PATH : loginUrlFor(location)}
                />
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/forget-password" element={<ForgetPassword />} />
          </Routes>
        )}
      </ConfigProvider>
    </ThemePaletteContext.Provider>
  );
}

export default App;
